import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import MarketingLayout from '@/components/MarketingLayout';
import { usePageSEO } from '@/utils/seo';
import {
  ORGANIZATION_SCHEMA, FOUNDER_SCHEMA, WEBSITE_SCHEMA,
  makeBreadcrumb, makeArticle,
} from '@/utils/schema';
import { POSTS, getPostBySlug } from '@/data/blogPosts';
import { ArrowRight, Calendar } from '@phosphor-icons/react';

/**
 * Renders the array-of-blocks `body` shape from blogPosts.js into JSX.
 * Each block is one of: { h }, { h2 }, { p }, { internalLink }, { img },
 * { pullQuote }, { cta }, { divider }.
 */
function renderArticleBody(body) {
  return body.map((block, idx) => {
    if (block.h) {
      return (
        <p key={idx} className="text-xl sm:text-2xl font-semibold text-slate-900 leading-snug pt-2">
          {block.h}
        </p>
      );
    }
    if (block.h2) {
      return (
        <h2 key={idx} className="text-xl sm:text-2xl font-semibold text-slate-900 leading-snug pt-4">
          {block.h2}
        </h2>
      );
    }
    if (block.pullQuote) {
      return (
        <blockquote
          key={idx}
          data-testid={`blog-pull-quote-${idx}`}
          className="my-6 border-l-4 border-blue-500 bg-slate-50 rounded-r-lg px-5 py-4 text-lg sm:text-xl italic text-slate-800 leading-relaxed"
        >
          {`\u201C${block.pullQuote}\u201D`}
        </blockquote>
      );
    }
    if (block.img) {
      return (
        <figure key={idx} className="my-6" data-testid={`blog-inline-image-${idx}`}>
          <img
            src={block.img.src}
            alt={block.img.alt || ''}
            loading="lazy"
            className="w-full rounded-xl border border-slate-200 shadow-sm"
          />
          {block.img.caption && (
            <figcaption className="text-sm text-slate-500 text-center pt-2">
              {block.img.caption}
            </figcaption>
          )}
        </figure>
      );
    }
    if (block.divider) {
      return (
        <hr
          key={idx}
          data-testid={`blog-divider-${idx}`}
          className="my-8 border-slate-200"
        />
      );
    }
    if (block.cta) {
      return (
        <div
          key={idx}
          data-testid={`blog-cta-${idx}`}
          className="my-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6 sm:p-8 text-center space-y-4"
        >
          {block.cta.text && (
            <p className="text-lg sm:text-xl text-slate-100 leading-relaxed">
              {block.cta.text}
            </p>
          )}
          <Link
            to={block.cta.href || '/signup'}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 hover:bg-blue-400 text-white font-semibold rounded-lg transition-colors"
            data-testid={`blog-cta-button-${idx}`}
          >
            {block.cta.buttonText || 'Get Started'}
            <ArrowRight size={18} weight="bold" />
          </Link>
        </div>
      );
    }
    if (block.internalLink) {
      return (
        <p key={idx} className="pt-2">
          <Link
            to={block.internalLink.to}
            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium"
            data-testid={`blog-internal-link-${idx}`}
          >
            {block.internalLink.text}
            <ArrowRight size={14} weight="bold" />
          </Link>
        </p>
      );
    }
    return (
      <p key={idx} className="text-slate-700 leading-relaxed text-[17px]">
        {block.p}
      </p>
    );
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  // SEO must be called unconditionally — provide fallback content when post is missing.
  usePageSEO({
    title: post
      ? `${post.title} | PayGBot Blog`
      : 'Article Not Found | PayGBot Blog',
    description: post ? post.description : 'This article could not be found on the PayGBot blog.',
    path: `/blog/${slug || ''}`,
    keywords: post?.keywords,
    ogType: 'article',
    publishedDate: post?.publishedDate,
    modifiedDate: post?.publishedDate,
    jsonLd: post
      ? [
          ORGANIZATION_SCHEMA,
          FOUNDER_SCHEMA,
          WEBSITE_SCHEMA,
          makeArticle({
            title: post.title,
            description: post.description,
            slug: post.slug,
            datePublished: post.publishedDate,
            dateModified: post.publishedDate,
          }),
          makeBreadcrumb([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title, path: `/blog/${post.slug}` },
          ]),
        ]
      : [],
  });

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const otherPosts = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <MarketingLayout>
      <article data-testid={`blog-article-${post.slug}`}>
        <section className="px-4 sm:px-6 lg:px-8 pt-12 pb-6 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-2xl mx-auto space-y-3">
            <Link to="/blog" className="text-sm text-blue-600 hover:text-blue-700">← All essays</Link>
            <h1 data-testid="blog-article-h1" className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 tracking-tight leading-tight">
              {post.title}
            </h1>
            <div className="flex items-center text-sm text-slate-500 gap-2 pt-1">
              <Calendar size={14} weight="regular" />
              <time dateTime={post.publishedDate}>
                {new Date(post.publishedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </time>
              <span>·</span>
              <span>By Vickie Mcglawn</span>
            </div>
          </div>
        </section>

        <section className="px-4 sm:px-6 lg:px-8 pb-14 bg-white">
          <div className="max-w-2xl mx-auto space-y-5">
            {post.heroImage && (
              <figure className="pt-4" data-testid={`blog-hero-image-${post.slug}`}>
                <img
                  src={post.heroImage.src}
                  alt={post.heroImage.alt || post.title}
                  className="w-full rounded-2xl border border-slate-200 shadow-md"
                />
                {post.heroImage.caption && (
                  <figcaption className="text-sm text-slate-500 text-center pt-2">
                    {post.heroImage.caption}
                  </figcaption>
                )}
              </figure>
            )}
            {renderArticleBody(post.body)}
          </div>
        </section>

        <section className="px-4 sm:px-6 lg:px-8 py-12 bg-slate-50 border-t border-slate-200">
          <div className="max-w-2xl mx-auto text-center space-y-3">
            <h2 className="text-xl sm:text-2xl font-semibold text-slate-900">Ready to stop reading and start building?</h2>
            <p className="text-slate-600">PayGBot is the AI business operating system that puts this entire approach into practice — in minutes a day.</p>
            <div className="flex flex-wrap justify-center gap-3 pt-2">
              <Link to="/signup" className="inline-flex items-center space-x-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg">
                <span>Start My Free Trial</span>
                <ArrowRight size={18} weight="bold" />
              </Link>
              <Link to="/what-is-paygbot" className="inline-flex items-center px-5 py-3 border border-slate-300 hover:border-slate-400 text-slate-700 font-medium rounded-lg">
                See How PayGBot Works
              </Link>
            </div>
          </div>
        </section>

        <section className="px-4 sm:px-6 lg:px-8 py-12 bg-white">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">More essays</h3>
            <div className="grid sm:grid-cols-3 gap-3">
              {otherPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="block border border-slate-200 rounded-xl p-4 hover:border-blue-300 hover:shadow-sm transition-all">
                  <p className="text-xs text-slate-500 mb-1">{new Date(p.publishedDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
                  <p className="font-semibold text-slate-900 text-sm leading-snug">{p.title}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </article>
    </MarketingLayout>
  );
}
